import { atom, useAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { Tenant } from '../components/dashboard/sysadmin/sysadmin-tenant/types/tenant.types';
import { LocalStorageUtil } from '../hooks/use-local-storage.hook';
import { ACTIVE_TENANT_STORAGE_KEY } from '../const/local-storage-keys.const';

// Helper function to load tenant from localStorage
const loadTenantFromStorage = (): Tenant | null => {
  try {
    return LocalStorageUtil.getItem<Tenant>(ACTIVE_TENANT_STORAGE_KEY);
  } catch (error) {
    console.error('Error loading tenant from localStorage:', error);
    return null;
  }
};

// Helper function to save tenant to localStorage
const saveTenantToStorage = (tenant: Tenant | null): void => {
  try {
    if (tenant) {
      LocalStorageUtil.setItem(ACTIVE_TENANT_STORAGE_KEY, tenant);
    } else {
      LocalStorageUtil.removeItem(ACTIVE_TENANT_STORAGE_KEY);
    }
  } catch (error) {
    console.error('Error saving tenant to localStorage:', error);
  }
};

// Create the active tenant atom (initialize with null, will be loaded from localStorage in hook)
export const activeTenantAtom = atom<Tenant | null>(null);

// Custom hook for active tenant management with localStorage persistence
export const useActiveTenant = () => {
  const [activeTenant, setActiveTenant] = useAtom(activeTenantAtom);
  const hasInitialized = useRef(false);

  // Load tenant from localStorage on first mount
  useEffect(() => {
    if (!hasInitialized.current) {
      const storedTenant = loadTenantFromStorage();
      if (storedTenant) {
        setActiveTenant(storedTenant);
      }
      hasInitialized.current = true;
    }
  }, [setActiveTenant]);

  // Sync to localStorage whenever activeTenant changes (but skip initial load)
  useEffect(() => {
    if (hasInitialized.current) {
      saveTenantToStorage(activeTenant);
    }
  }, [activeTenant]);

  return { activeTenant, setActiveTenant };
};
